import {
  Children,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ElementType,
  type ReactNode,
  type JSX,
  type Key,
  type ContextType,
} from 'react'
import { jsx as originalJsx, jsxs as originalJsxs, Fragment } from 'react/jsx-runtime'
import { jsxDEV as originalJsxDEV, type JSXSource } from 'react/jsx-dev-runtime'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ChatDisplayContext, ChatDisplayExcluded } from './ChatDisplayScope'
import ReactMarkdown from 'react-markdown'
import type { PresentedText } from './workOrderPresentation'
import { createChatDisplaySubscription } from './chatDisplaySubscription'
import { reportPresentation } from './presentationDiagnostics'
export { Fragment }
export type { JSX } from 'react'

type Props = Record<string, unknown> & { children?: ReactNode }
const EXCLUDED = new Set(['pre', 'code', 'script', 'style', 'svg', 'math'])
const URL = /https?:\/\/\S+|\b[\w.+-]+@[\w.-]+\.[a-z]{2,}\b/gi
const MARKUP =
  /```[\s\S]*?```|`[^`\n]+`|\[(?:doc|wo|norma|equip):[^\]]+\]|\]\([^)]*\)|\[|(?:^|\n)[ \t]*(?:#{1,6} |> |[-+*] |\d+\. )|[*_~]{1,3}|\||\n/g
const emptySnapshot = () => ''
/**
 * R204: um lote de tradução que falha (rede, 429, tempo esgotado) deixava as linhas dele
 * em inglês PARA SEMPRE — o resultado "indisponível" nunca era pedido de novo até a bolha
 * remontar. Agora o texto volta a ser pedido sozinho, com espera crescente; a última
 * espera passa do resfriamento de 30 s do 429 (chatPresentationTransport).
 */
export const DISPLAY_RETRY_DELAYS_MS = [2_000, 8_000, 35_000] as const

function useDisplayRetention(
  presenter: ContextType<typeof ChatDisplayContext>,
  source: string[],
  locale: string | undefined,
  translation: { current: TFunction },
) {
  const observed = useMemo(
    () => createChatDisplaySubscription(presenter, source, locale, translation),
    [presenter, source, locale, translation],
  )
  useSyncExternalStore(observed.subscribe, observed.getSnapshot, emptySnapshot)
  return observed.retained
}

type DisplayResult = {
  values: PresentedText[]
  locale: string
  key: string
  presenter: ContextType<typeof ChatDisplayContext>
}
function matchingResult(
  result: DisplayResult | undefined,
  enabled: boolean,
  key: string,
  locale: string | undefined,
  presenter: ContextType<typeof ChatDisplayContext>,
) {
  return enabled &&
    result?.key === key &&
    result.locale === locale &&
    result.presenter === presenter
    ? result
    : undefined
}

interface ShownArgs {
  values: string[]
  source: string[]
  enabled: boolean
  current: DisplayResult | undefined
  presenter: ContextType<typeof ChatDisplayContext>
  locale: string | undefined
  retained: ReturnType<typeof useDisplayRetention>
  translation: { current: ReturnType<typeof useTranslation>['t'] }
}

function shownStrings({ values, source, enabled, current, presenter, locale, retained, translation }: ShownArgs) {
  const detail: { cachedState: string; retained: boolean; resultState: string }[] = []
  const shown =
    !enabled || !current
      ? values
      : source.map((value, index) => {
          const cached =
            presenter && locale
              ? presenter.present({ kind: 'text', value }, locale, translation.current)
              : undefined
          const kept = retained.get(value)
          detail[index] = {
            cachedState: cached?.state ?? 'none',
            retained: kept !== undefined,
            resultState: current.values[index].state,
          }
          if (cached?.state === 'translated') return cached.text
          return kept ?? current.values[index].text
        })
  return { shown, detail }
}

function useDisplayStrings(values: string[], enabled = true) {
  const presenter = useContext(ChatDisplayContext)
  const { i18n, t } = useTranslation()
  const translation = useRef(t)
  translation.current = t
  const locale = i18n?.language ?? i18n?.resolvedLanguage
  const key = JSON.stringify(values)
  const source = useMemo(() => JSON.parse(key) as string[], [key])
  const retained = useDisplayRetention(presenter, source, locale, translation)
  const [result, setResult] = useState<DisplayResult>()
  const [retry, setRetry] = useState(0)
  const retries = useRef({ scope: '', used: 0 })
  useEffect(() => {
    if (!presenter || !locale || !enabled || !source.length) return
    let current = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const scope = `${locale}\u0000${key}`
    if (retries.current.scope !== scope) retries.current = { scope, used: 0 }
    void Promise.all(
      source.map((text) =>
        presenter.resolve(
          {
            kind: /^\s*(?:\{|\[\s*(?:\{|"))/.test(text) ? 'identity' : 'text',
            value: text,
            protectedValues: [...(text.match(URL) ?? []), ...(text.match(MARKUP) ?? [])],
          },
          locale,
          translation.current,
        ),
      ),
    ).then((texts) => {
      if (!current) return
      setResult({ values: texts, locale, key, presenter })
      const delay = DISPLAY_RETRY_DELAYS_MS[retries.current.used]
      if (delay !== undefined && texts.some(({ state }) => state === 'unavailable')) {
        retries.current.used++
        timer = setTimeout(() => setRetry((count) => count + 1), delay)
      }
    })
    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [presenter, source, key, locale, enabled, retry])
  const current = matchingResult(result, enabled, key, locale, presenter)
  const { shown, detail } = shownStrings({ values, source, enabled, current, presenter, locale, retained, translation })
  // R210: medir o instante em que um texto já traduzido volta ao original no mesmo componente.
  const last = useRef<{ scope: string; presenter: unknown; out: string[]; at: number } | undefined>(
    undefined,
  )
  useEffect(() => {
    const scope = `${locale}\u0000${key}`
    const before = last.current
    const same = before?.scope === scope && before.presenter === presenter
    last.current = { scope, presenter, out: shown, at: same ? before.at : Date.now() }
    if (!same) return
    source.forEach((value, index) => {
      if (before.out[index] !== value && shown[index] === value && detail[index])
        reportPresentation({
          event: 'reverted',
          text: value,
          ...detail[index],
          ageMs: Date.now() - before.at,
        })
    })
  })
  return shown
}
const ChatDisplayText = ({ text }: { text: string }) => useDisplayStrings([text])[0]
function ChatMarkdown(props: Props) {
  const presenter = useContext(ChatDisplayContext)
  const excluded = useContext(ChatDisplayExcluded)
  return !presenter || excluded
    ? originalJsx(ReactMarkdown, props)
    : originalJsx(ChatActiveMarkdown, props)
}
function ChatActiveMarkdown(props: Props) {
  const display = useDisplayStrings(typeof props.children === 'string' ? [props.children] : [])
  return originalJsx(ReactMarkdown, { ...props, children: display[0] ?? props.children })
}

/** React owns every text node; no DOM mutation, command projection or event interception. */
function ChatIntrinsic({ type, original }: { type: keyof JSX.IntrinsicElements; original: Props }) {
  const presenter = useContext(ChatDisplayContext)
  const excluded = useContext(ChatDisplayExcluded)
  if (!presenter || excluded) return originalJsx(type, original)
  return originalJsx(ChatActiveIntrinsic, { type, original })
}

function ChatActiveIntrinsic({
  type,
  original,
}: {
  type: keyof JSX.IntrinsicElements
  original: Props
}) {
  const skip =
    EXCLUDED.has(type) ||
    original['translate'] === 'no' ||
    Boolean(original['data-chat-no-translate'])
  const attributes = ['title', 'aria-label', 'placeholder'].filter(
    (key) => typeof original[key] === 'string',
  )
  const explicitOption =
    type === 'option' && original['value'] !== undefined && typeof original.children === 'string'
  const values = [
    ...attributes.map((key) => original[key] as string),
    ...(explicitOption ? [original.children as string] : []),
  ]
  const display = useDisplayStrings(values, !skip)
  if (skip || type === 'option' || type === 'input' || type === 'textarea') {
    const props = {
      ...original,
      ...Object.fromEntries(attributes.map((key, index) => [key, display[index]])),
      ...(explicitOption ? { children: display[attributes.length] } : {}),
    }
    return originalJsx(ChatDisplayExcluded.Provider, {
      value: true,
      children: originalJsx(type, props),
    })
  }
  const children = Children.map(original.children, (child) =>
    typeof child === 'string' && /[\p{L}]/u.test(child)
      ? originalJsx(ChatDisplayText, { text: child })
      : child,
  )
  return originalJsx(type, {
    ...original,
    children,
    ...Object.fromEntries(attributes.map((key, index) => [key, display[index]])),
  })
}

export function jsx(type: ElementType, props: Props, key?: Key) {
  return typeof type === 'string'
    ? originalJsx(intrinsicComponent(type), props, key)
    : originalJsx(type === ReactMarkdown ? ChatMarkdown : type, props, key)
}
const intrinsics = new Map<keyof JSX.IntrinsicElements, (props: Props) => ReactNode>()
function intrinsicComponent(type: keyof JSX.IntrinsicElements) {
  let component = intrinsics.get(type)
  if (!component) {
    component = (props: Props) => originalJsx(ChatIntrinsic, { type, original: props })
    intrinsics.set(type, component)
  }
  return component
}
export const jsxs = (type: ElementType, props: Props, key?: Key) =>
  originalJsxs(
    typeof type === 'string'
      ? intrinsicComponent(type)
      : type === ReactMarkdown
        ? ChatMarkdown
        : type,
    props,
    key,
  )
/** Preserve React's development source/self and static-child diagnostics. */
export const jsxDEV = (
  type: ElementType,
  props: Props,
  key: Key | undefined,
  isStatic: boolean,
  source?: JSXSource,
  self?: unknown,
) =>
  originalJsxDEV(
    typeof type === 'string'
      ? intrinsicComponent(type)
      : type === ReactMarkdown
        ? ChatMarkdown
        : type,
    props,
    key,
    isStatic,
    source,
    self,
  )
